import React, { useEffect, useState, useCallback } from 'react';
import { ModernNavigation } from '../components/modern/ModernNavigation';
import { ModernFooter } from '../components/modern/ModernFooter';
import '../css/modern-design-system.css';
import '../css/modern-landing.css';

// Hero slide type
interface HeroSlide {
  id: number;
  badge: { icon: string; text: string };
  title: [string, string];
  subtitle: string;
  stats: Array<{ value: string; label: string }>;
  cta: { primary: string; secondary: string };
}

// Hero slide data
const heroSlides: HeroSlide[] = [
  {
    id: 1,
    badge: { icon: '🛡️', text: 'Protect Your Finances' },
    title: ['Protect Your Home,', 'Grow Your Wealth'],
    subtitle: 'Get personalized insights that help you avoid unnecessary fees, protect your credit score, and discover savings opportunities before they pass you by.',
    stats: [
      { value: '$847', label: 'Avg. Yearly Savings' },
      { value: '100%', label: 'Fee Transparency' },
      { value: 'Real-Time', label: 'Credit Monitoring' },
    ],
    cta: { primary: 'Protect My Finances', secondary: 'See How It Works' },
  },
  {
    id: 2,
    badge: { icon: '🚀', text: 'Transform Your Financial Future' },
    title: ['Turn Your Mortgage', 'Into Millions'],
    subtitle: 'Join 50,000+ homeowners using EquityWorkx\'s revolutionary platform to build generational wealth through smart mortgage optimization and personalized financial insights.',
    stats: [
      { value: '$2.4M', label: 'Avg. Wealth Built' },
      { value: '98%', label: 'Success Rate' },
      { value: '15 Years', label: 'Faster to $1M' },
    ],
    cta: { primary: 'Start Building Wealth', secondary: 'Watch Demo' },
  },
  {
    id: 3,
    badge: { icon: '💡', text: 'Smart Refinancing Insights' },
    title: ['Know When to', 'Refinance & Save'],
    subtitle: 'Stop guessing about refinancing. Our intelligent platform monitors rates and your equity position to alert you at the perfect moment to save thousands on your mortgage.',
    stats: [
      { value: '$12,400', label: 'Avg. Refi Savings' },
      { value: '24/7', label: 'Rate Monitoring' },
      { value: 'Personalized', label: 'Recommendations' },
    ],
    cta: { primary: 'Get Refinance Alerts', secondary: 'Learn More' },
  },
];

function ModernApp() {
  const [scrolled, setScrolled] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Auto-rotate slides every 4 seconds
  useEffect(() => {
    if (!isAutoPlaying) return;

    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
    }, 4000);

    return () => clearInterval(interval);
  }, [isAutoPlaying]);

  const goToSlide = useCallback((index: number) => {
    setCurrentSlide(index);
    setIsAutoPlaying(false);
    // Resume auto-play after 10 seconds of inactivity
    setTimeout(() => setIsAutoPlaying(true), 10000);
  }, []);

  // Non-null assertion is safe here since currentSlide is always within bounds (0 to heroSlides.length-1)
  const currentHero = heroSlides[currentSlide]!;

  return (
    <div className="modern-app">
      <ModernNavigation scrolled={scrolled} />

      {/* Hero Section with Slider */}
      <section className="hero-modern">
        <div className="hero-background">
          <div className="gradient-orb gradient-orb-1"></div>
          <div className="gradient-orb gradient-orb-2"></div>
          <div className="gradient-orb gradient-orb-3"></div>
          <div className="grid-pattern"></div>
        </div>

        <div className="container hero-container">
          <div className="hero-content-modern">
            <div className="hero-text-section" key={currentHero.id}>
              <div className="hero-badge hero-slide-animate">
                <span className="badge-icon">{currentHero.badge.icon}</span>
                <span>{currentHero.badge.text}</span>
              </div>

              <h1 className="hero-title-modern hero-slide-animate">
                {currentHero.title[0]}
                <br />
                <span className="text-gradient">{currentHero.title[1]}</span>
              </h1>

              <p className="hero-subtitle-modern hero-slide-animate">
                {currentHero.subtitle}
              </p>

              <div className="hero-stats hero-slide-animate">
                {currentHero.stats.map((stat, index) => (
                  <div className="stat-item" key={index}>
                    <span className="stat-value">{stat.value}</span>
                    <span className="stat-label">{stat.label}</span>
                  </div>
                ))}
              </div>

              <div className="hero-cta-group hero-slide-animate">
                <a href="/download" className="btn btn-primary btn-lg">
                  <span>{currentHero.cta.primary}</span>
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                    <path d="M7 10H13M13 10L10 7M13 10L10 13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </a>
                <a href="#demo" className="btn btn-secondary btn-lg">
                  <span className="play-icon">▶</span>
                  <span>{currentHero.cta.secondary}</span>
                </a>
              </div>

              <div className="hero-trust-badges hero-slide-animate">
                <img src="https://img.shields.io/badge/App_Store-5_Stars-blue" alt="App Store Rating" />
                <img src="https://img.shields.io/badge/Bank_Level-Security-green" alt="Security Badge" />
              </div>

              {/* Slide Indicators */}
              <div className="hero-slide-indicators">
                {heroSlides.map((_, index) => (
                  <button
                    key={index}
                    className={`slide-indicator ${index === currentSlide ? 'active' : ''}`}
                    onClick={() => goToSlide(index)}
                    aria-label={`Go to slide ${index + 1}`}
                  >
                    <span className="indicator-progress"></span>
                  </button>
                ))}
              </div>
            </div>

            <div className="hero-visual-section animate-slideInRight">
              <div className="phone-mockup-modern">
                <div className="phone-frame">
                  <div className="phone-notch"></div>
                  <div className="phone-screen">
                    <img src="/app-dash.png" alt="EquityWorkx App" className="app-screenshot-modern" />
                    <div className="screen-overlay"></div>
                  </div>
                </div>
                <div className="floating-cards">
                  <div className="floating-card card-1">
                    <div className="card-icon">📈</div>
                    <div className="card-content">
                      <span className="card-label">Portfolio Growth</span>
                      <span className="card-value">+34.2%</span>
                    </div>
                  </div>
                  <div className="floating-card card-2">
                    <div className="card-icon">💰</div>
                    <div className="card-content">
                      <span className="card-label">Equity Built</span>
                      <span className="card-value">$450,461</span>
                    </div>
                  </div>
                  <div className="floating-card card-3">
                    <div className="card-icon">⚡</div>
                    <div className="card-content">
                      <span className="card-label">Intelligent Insights</span>
                      <span className="card-value">Cash Flow</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="hero-scroll-indicator">
          <span>Scroll to explore</span>
          <div className="scroll-arrow"></div>
        </div>
      </section>

      {/* Features Section */}
      <section className="features-modern">
        <div className="container">
          <div className="section-header-modern">
            <span className="section-label">POWERFUL FEATURES</span>
            <h2 className="section-title-modern">
              Everything You Need to<br /><span className="text-gradient">Build Wealth</span>
            </h2>
            <p className="section-subtitle-modern">
              Our comprehensive platform combines cutting-edge technology with proven wealth-building strategies
            </p>
          </div>

          <div className="features-grid-modern">
            <div className="feature-card-modern">
              <div className="feature-icon-modern">
                <div className="icon-bg"></div>
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
                  <path d="M3 12L5 10M5 10L12 3L19 10M5 10V20C5 20.5523 5.44772 21 6 21H9M19 10L21 12M19 10V20C19 20.5523 18.5523 21 18 21H15M9 21C9.55228 21 10 20.5523 10 20V16C10 15.4477 10.4477 15 11 15H13C13.5523 15 14 15.4477 14 16V20C14 20.5523 14.4477 21 15 21M9 21H15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <h3>Smart Mortgage Tracking</h3>
              <p>Real-time insights into your mortgage performance with data-driven optimization recommendations.</p>
              <span className="feature-link">
                Learn more <span>→</span>
              </span>
            </div>

            <div className="feature-card-modern">
              <div className="feature-icon-modern">
                <div className="icon-bg"></div>
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
                  <path d="M9 7H9.01M15 7H15.01M9 12V13M15 12V13M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <h3>Wealth Calculators</h3>
              <p>Advanced calculators for mortgage scenarios, investments, and the "Mortgage Millionaire" strategy.</p>
              <span className="feature-link">
                Explore tools <span>→</span>
              </span>
            </div>

            <div className="feature-card-modern">
              <div className="feature-icon-modern">
                <div className="icon-bg"></div>
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
                  <path d="M12 2V6M12 6L15 3M12 6L9 3M3.5 12C3.5 7.30558 7.30558 3.5 12 3.5C16.6944 3.5 20.5 7.30558 20.5 12C20.5 16.6944 16.6944 20.5 12 20.5C7.30558 20.5 3.5 16.6944 3.5 12ZM12 8V12L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <h3>Cash Flow Analysis</h3>
              <p>Automated spending categorization and optimization to maximize your wealth-building potential.</p>
              <span className="feature-link">
                View analytics <span>→</span>
              </span>
            </div>

            <div className="feature-card-modern">
              <div className="feature-icon-modern">
                <div className="icon-bg"></div>
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
                  <path d="M13 7L11.8845 4.76892C11.5634 4.1268 11.403 3.80573 11.1573 3.59116C10.9116 3.37659 10.6085 3.28677 10.3025 3.33679C9.99654 3.38681 9.70644 3.56105 9.12624 3.90954L3.9 7.2C3.53333 7.42222 3.35 7.53333 3.21833 7.68944C3.08666 7.84556 3.01334 8.03667 2.99917 8.23778C2.985 8.43889 3.03333 8.65111 3.13 9.07556L4.245 13.6278C4.34167 14.0522 4.39 14.2644 4.49917 14.4361C4.60833 14.6078 4.76833 14.7294 4.95167 14.7811C5.135 14.8328 5.34333 14.81 5.76 14.7644L9.87624 14.3149C10.3262 14.2654 10.5512 14.2407 10.7573 14.1537C10.9634 14.0667 11.1398 13.9218 11.2648 13.7362C11.3898 13.5506 11.4567 13.3144 11.5905 12.842L13 7.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M13 7L18.2262 4.38682C18.5762 4.20573 18.7512 4.11519 18.9402 4.08388C19.1293 4.05257 19.3199 4.08189 19.4889 4.16784C19.6579 4.25379 19.7984 4.39432 20.0795 4.67538L20.3243 4.92025C20.6054 5.20131 20.7459 5.34184 20.8319 5.51084C20.9178 5.67985 20.9472 5.87041 20.9159 6.05944C20.8845 6.24846 20.794 6.42348 20.6129 6.77351L18 12L12.5 13L13 7Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <h3>Personalized Insights</h3>
              <p>Personalized recommendations tailored to your unique financial situation and goals.</p>
              <span className="feature-link">
                Get insights <span>→</span>
              </span>
            </div>

            <div className="feature-card-modern">
              <div className="feature-icon-modern">
                <div className="icon-bg"></div>
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
                  <path d="M22 12C22 17.5228 17.5228 22 12 22M22 12C22 6.47715 17.5228 2 12 2M22 12H20M12 22C6.47715 22 2 17.5228 2 12M12 22V20M2 12C2 6.47715 6.47715 2 12 2M2 12H4M12 2V4M15 12C15 13.6569 13.6569 15 12 15C10.3431 15 9 13.6569 9 12C9 10.3431 10.3431 9 12 9C13.6569 9 15 10.3431 15 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <h3>Goal Tracking</h3>
              <p>Set and monitor financial milestones with behavioral insights to stay on track.</p>
              <span className="feature-link">
                Set goals <span>→</span>
              </span>
            </div>

            <div className="feature-card-modern">
              <div className="feature-icon-modern">
                <div className="icon-bg"></div>
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
                  <path d="M9 12.75L11.25 15L15 9.75M21 12C21 13.1819 20.7672 14.3522 20.3149 15.4442C19.8626 16.5361 19.1997 17.5282 18.364 18.364C17.5282 19.1997 16.5361 19.8626 15.4442 20.3149C14.3522 20.7672 13.1819 21 12 21C10.8181 21 9.64778 20.7672 8.55585 20.3149C7.46392 19.8626 6.47177 19.1997 5.63604 18.364C4.80031 17.5282 4.13738 16.5361 3.68508 15.4442C3.23279 14.3522 3 13.1819 3 12C3 9.61305 3.94821 7.32387 5.63604 5.63604C7.32387 3.94821 9.61305 3 12 3C14.3869 3 16.6761 3.94821 18.364 5.63604C20.0518 7.32387 21 9.61305 21 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <h3>Bank-Level Security</h3>
              <p>Your financial data is protected with enterprise-grade encryption and security protocols.</p>
              <span className="feature-link">
                Security details <span>→</span>
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="how-it-works-modern">
        <div className="container">
          <div className="section-header-modern">
            <span className="section-label">SIMPLE PROCESS</span>
            <h2 className="section-title-modern">
              Start Building Wealth in <span className="text-gradient">3 Simple Steps</span>
            </h2>
          </div>

          <div className="steps-container">
            <div className="step-card">
              <div className="step-number">01</div>
              <div className="step-icon">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
                  <path d="M12 2L2 7L12 12L22 7L12 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M2 17L12 22L22 17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M2 12L12 17L22 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <h3>Connect Your Accounts</h3>
              <p>Securely link your mortgage and financial accounts with bank-level encryption.</p>
            </div>

            <div className="step-connector"></div>

            <div className="step-card">
              <div className="step-number">02</div>
              <div className="step-icon">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
                  <path d="M9 11L12 14L22 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M21 12V19C21 19.5304 20.7893 20.0391 20.4142 20.4142C20.0391 20.7893 19.5304 21 19 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V5C3 4.46957 3.21071 3.96086 3.58579 3.58579C3.96086 3.21071 4.46957 3 5 3H16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <h3>Get Personalized Plan</h3>
              <p>Receive personalized insights and a customized wealth-building strategy.</p>
            </div>

            <div className="step-connector"></div>

            <div className="step-card">
              <div className="step-number">03</div>
              <div className="step-icon">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
                  <path d="M22 12H18L15 21L9 3L6 12H2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <h3>Track & Optimize</h3>
              <p>Monitor your progress and optimize your strategy with real-time recommendations.</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-modern">
        <div className="container">
          <div className="cta-content-modern">
            <h2 className="cta-title-modern">
              Ready to Transform Your <span className="text-gradient">Financial Future?</span>
            </h2>
            <p className="cta-subtitle-modern">
              Join thousands of homeowners who are building generational wealth with EquityWorkx.
              Start your journey today with our free wealth assessment.
            </p>
            <div className="cta-buttons-modern">
              <a href="/download" className="btn btn-primary btn-lg">
                Get Started Free
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path d="M7 10H13M13 10L10 7M13 10L10 13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </a>
              <a href="/agent/login" className="btn btn-secondary btn-lg">
                Agent Portal
              </a>
            </div>
            <div className="cta-features">
              <div className="cta-feature">
                <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
                </svg>
                <span>Free to start</span>
              </div>
              <div className="cta-feature">
                <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
                </svg>
                <span>No credit card required</span>
              </div>
              <div className="cta-feature">
                <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
                </svg>
                <span>Bank-level security</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <ModernFooter />
    </div>
  );
}

export default ModernApp;
